import { Stats } from 'src/game/stats';
import { track } from 'src/lib/analytics/utils';
import { setPuzzleUserProps } from 'src/lib/setupUserProps';
import { user } from 'src/StateObserver';
import { getDreamDriftPuzzleEventProperties } from 'src/replicant/dreamDrift/dreamDrift.analytics';
import { dreamDriftGetCheckpoint } from 'src/replicant/dreamDrift/dreamDrift.getters';
import type { PuzzleResults } from 'src/replicant/match2/match2.ruleset';
import type { GameScene } from 'src/game/minigame/match2-timestep/game/GameScene';
import {
  getPuzzleFinishData,
  getPuzzleStartData,
  type PuzzleStartData,
} from 'src/game/minigame/match2-timestep/match2.analytics';
import { getRaceAnalytics } from 'src/game/minigame/match2-timestep/races/races.analytics';

/**
 * Track the start of a Dream Drift puzzle using the shared Match-2 properties.
 *
 * @param game Initialized Dream Drift Match-2 runtime.
 * @returns Puzzle start data reused by the finish event, or undefined without an active instance.
 */
export function trackDreamDriftPuzzleStart(
  game: GameScene,
): PuzzleStartData | undefined {
  // A loaded map and active Dream Drift instance are required for attribution.
  const puzzleEventProperties = getDreamDriftPuzzleEventProperties(user());
  if (!game.mapDef || !puzzleEventProperties) return undefined;

  // Reuse the standard Match-2 start properties without changing its sender.
  const puzzleStartData = getPuzzleStartData(game.level, game.mapDef);
  track('PuzzleStart', {
    ...puzzleStartData,
    ...puzzleEventProperties,
  });

  // Retain the shared start snapshot for the matching finish event.
  return puzzleStartData;
}

/**
 * Track the finish of a Dream Drift puzzle using the shared Match-2 properties.
 *
 * @param game Completed Dream Drift Match-2 runtime.
 * @param puzzleStartData Start snapshot captured for this attempt.
 * @returns Nothing.
 */
export function trackDreamDriftPuzzleFinish(
  game: GameScene,
  puzzleStartData: PuzzleStartData,
): void {
  // The active node is required to calculate loss-resume attribution.
  const puzzleEventProperties = getDreamDriftPuzzleEventProperties(user());
  if (!puzzleEventProperties) return;

  const dreamNodeIndex = puzzleEventProperties.dreamNodeIndex;

  // Normalize broken levels to the quit outcome used by Dream Drift progression.
  const result = game.getResult();
  const dreamDriftResult = result === 'broken' ? 'quit' : result;
  const puzzleResults: PuzzleResults = {
    ...game.getPuzzleResults(),
    result: dreamDriftResult,
  };

  // Reuse the standard Match-2 finish calculation without its main-map side effects.
  const puzzleFinishData = getPuzzleFinishData(
    game.level,
    [],
    puzzleStartData,
    puzzleResults,
    1,
    game.dynamicBlockGenType,
  );
  const racesAnalytics = getRaceAnalytics();
  const attemptsStats = Stats.api.getPuzzleAttemptsStats(user());

  // Emit the complete Dream Drift finish event before the instance is reconciled.
  track('PuzzleFinish', {
    ...puzzleFinishData,
    ...(racesAnalytics.properties ?? {}),
    racePosition: racesAnalytics.getPosition?.(),
    usingSaveState: puzzleResults.usingSaveState,
    isCardDrop: false,
    ...attemptsStats,
    ...puzzleEventProperties,
    resumeNodeOnLoss:
      dreamDriftResult === 'win'
        ? undefined
        : dreamDriftGetCheckpoint(user(), dreamNodeIndex),
  });

  // Preserve the standard puzzle user-property update after PuzzleFinish.
  setPuzzleUserProps();
}
